import { ISize2 } from "@base/math/Size2";
import { inferRecord, mapRecordToArray } from "@base/util/TypeUtil";
import { BizFacade } from "@biz/BizFacade";
import { ImageSource, IMaterialSettings, ITexInfoBase } from "@biz/definitions/SunSailDefinitions";
import { IUserFileInfo } from "@biz/userProjects/new/IVariant";

export type PredefinedTextureCategory = "ground" | "wall" | "roof" | "opening" | "custom";
interface IPredefinedTextureMaterial {
  category?: PredefinedTextureCategory;
  materialSettings?: IMaterialSettings;
  type: "pattern" | "image";
  size?: ISize2;
}

const materialSettingTemplates = inferRecord<IMaterialSettings>()({
  grassLike: {
    color: "#b6c999",
    metalness: 0.2,
    roughness: 0.5,
    envMapIntensity: 0.8,
    normalScale: 2.4
  },
  brickLike: {
    metalness: 1.0,
    roughness: 0.9,
    envMapIntensity: 0.8,
    normalScale: 2.0
  },
  marbleLike: {
    metalness: 1.0,
    roughness: 0.7,
    envMapIntensity: 0.8,
    normalScale: 2.0
  },
  stoneLike: {
    metalness: 0.0,
    roughness: 1.0,
    envMapIntensity: 0.8,
    normalScale: 2.0
  },
  putzLike: {
    metalness: 0.0,
    roughness: 1.0,
    envMapIntensity: 0.8,
    normalScale: 2.0
  },
  woodLike: {
    metalness: 1.0,
    roughness: 1.0,
    envMapIntensity: 0.8,
    normalScale: 3.0
  },
  roofLike: {
    metalness: 1.0,
    roughness: 1.0,
    envMapIntensity: 0.8,
    normalScale: 3.0,
    blend: "overlay"
  },
  doorLike: {
    metalness: 0.5,
    roughness: 0.5,
    envMapIntensity: 0.6,
  },
  windowLike: {
    metalness: 0.0,
    roughness: 0.6,
    envMapIntensity: 0.6,
    blend: "alphaComposite"
  }
})

const predefinedMap = {
  asphalt: { category: "ground", materialSettings: materialSettingTemplates.stoneLike, type: "pattern" },
  brick1: { category: "wall", materialSettings: materialSettingTemplates.brickLike, type: "pattern" },
  brick2: { category: "wall", materialSettings: materialSettingTemplates.brickLike, type: "pattern" },
  brick3: { category: "wall", materialSettings: materialSettingTemplates.brickLike, type: "pattern" },
  brick4: { category: "wall", materialSettings: materialSettingTemplates.brickLike, type: "pattern" },
  concrete2: { category: "wall", type: "pattern" },
  grass: { category: "ground", materialSettings: materialSettingTemplates.grassLike, type: "pattern" },
  grass2: { category: "ground", materialSettings: materialSettingTemplates.grassLike, type: "pattern" },
  groundTile: { category: "ground", type: "pattern" },
  marble: { category: "ground", materialSettings: materialSettingTemplates.marbleLike, type: "pattern" },
  marble2: { category: "ground", materialSettings: materialSettingTemplates.marbleLike, type: "pattern" },
  marble3: { category: "ground", materialSettings: materialSettingTemplates.marbleLike, type: "pattern" },
  marble4: { category: "ground", materialSettings: materialSettingTemplates.marbleLike, type: "pattern" },
  putz1: { category: "wall", materialSettings: materialSettingTemplates.putzLike, type: "pattern" },
  putz2: { category: "wall", materialSettings: materialSettingTemplates.putzLike, type: "pattern" },
  stone: { category: "ground", materialSettings: materialSettingTemplates.stoneLike, type: "pattern" },
  stone2: { category: "ground", materialSettings: materialSettingTemplates.stoneLike, type: "pattern" },
  wood: { category: "ground", materialSettings: materialSettingTemplates.woodLike, type: "pattern" },
  woodDark: { category: "ground", materialSettings: materialSettingTemplates.woodLike, type: "pattern" },
  woodNew: { category: "ground", materialSettings: materialSettingTemplates.woodLike, type: "pattern" },
  woodOld: { category: "ground", materialSettings: materialSettingTemplates.woodLike, type: "pattern" },
  wpc: { category: "ground", materialSettings: materialSettingTemplates.woodLike, type: "pattern" },
  wpc2: { category: "ground", materialSettings: materialSettingTemplates.woodLike, type: "pattern" },
  water: { category: "ground", type: "pattern" },
  roof1: { category: "roof", materialSettings: materialSettingTemplates.roofLike, type: "pattern" },
  roof2: { category: "roof", materialSettings: materialSettingTemplates.roofLike, type: "pattern" },
  roof3: { category: "roof", materialSettings: materialSettingTemplates.roofLike, type: "pattern" },
  roof4: { category: "roof", materialSettings: materialSettingTemplates.roofLike, type: "pattern" },
  roof5: { category: "roof", materialSettings: materialSettingTemplates.roofLike, type: "pattern" },
  roof6: { category: "roof", materialSettings: materialSettingTemplates.roofLike, type: "pattern" },
  opening1: { category: "opening", materialSettings: materialSettingTemplates.doorLike, type: "image", size: { w: 392, h: 786 } },
  opening2: { category: "opening", materialSettings: materialSettingTemplates.doorLike, type: "image", size: { w: 392, h: 769 } },
  opening3: { category: "opening", materialSettings: materialSettingTemplates.doorLike, type: "image", size: { w: 389, h: 759 } },
  opening4: { category: "opening", materialSettings: materialSettingTemplates.doorLike, type: "image", size: { w: 386, h: 703 } },
  opening5: { category: "opening", materialSettings: materialSettingTemplates.doorLike, type: "image", size: { w: 385, h: 753 } },
  window_01: { category: "opening", materialSettings: materialSettingTemplates.windowLike, type: "image", size: { w: 729, h: 586 } },
  window_02: { category: "opening", materialSettings: materialSettingTemplates.windowLike, type: "image", size: { w: 736, h: 737 } },
  window_03: { category: "opening", materialSettings: materialSettingTemplates.windowLike, type: "image", size: { w: 658, h: 805 } },
  window_04: { category: "opening", materialSettings: materialSettingTemplates.windowLike, type: "image", size: { w: 754, h: 755 } },
  window_05: { category: "opening", materialSettings: materialSettingTemplates.windowLike, type: "image", size: { w: 755, h: 755 } },
  window_06: { category: "opening", materialSettings: materialSettingTemplates.windowLike, type: "image", size: { w: 658, h: 805 } },
} satisfies Record<string, IPredefinedTextureMaterial>;

export type PredefinedImageId = keyof typeof predefinedMap;

export class PredefinedMaterials {
  static predefinedMaterialArray: ITextureMaterial[] = mapRecordToArray(predefinedMap, (m, id) => PredefinedMaterials.predefinedToTextureMaterial(id, m))

  static getUrl(id: PredefinedImageId, type: "image" | "normal"): string | undefined {
    return BizFacade.getUrl(id, type);
  }

  static getPredefinedMaterialById(id: PredefinedImageId): IPredefinedTextureMaterial | undefined {
    return predefinedMap[id];
  }

  static getPredefinedTextureMaterialById(id: PredefinedImageId): ITextureMaterial | undefined {
    const m = predefinedMap[id];
    return m ? PredefinedMaterials.predefinedToTextureMaterial(id, m) : undefined;
  }

  static getPredefinedMaterialForCategory(category: PredefinedTextureCategory): ITextureMaterial | undefined {
    for (const p of PredefinedMaterials.predefinedMaterialArray) {
      if (p.category === category)
        return p;
    }
    return undefined;
  }

  static getMaterialOptionsForImageSource(imageSource: ImageSource | undefined, defaultSettings: IMaterialSettings = PredefinedMaterials.getDefaultOptions()): IMaterialSettings {
    if (imageSource?.type === "predefined")
      return PredefinedMaterials.getMaterialOptions(imageSource.id);
    return defaultSettings;
  }

  static getDefaultOptions(): IMaterialSettings {
    return {
      metalness: 0,
      roughness: 1,
      envMapIntensity: 0.7,
      normalScale: 1.0,
    }
  }

  static getMaterialOptions(id: PredefinedImageId): IMaterialSettings {
    const tm = predefinedMap[id];
    const predefinedSettings = "materialSettings" in tm ? tm.materialSettings : undefined;
    if (predefinedSettings)
      return predefinedSettings;

    return PredefinedMaterials.getDefaultOptions();
  }

  static getMaterialArray(): ITextureMaterial[] {
    return PredefinedMaterials.predefinedMaterialArray;
  }

  private static predefinedToTextureMaterial(id: PredefinedImageId, predefined: IPredefinedTextureMaterial): ITextureMaterial {
    return {
      category: predefined.category,
      id,
      image: { type: "predefined", id },
      normal: PredefinedMaterials.getUrl(id, "normal") ? { type: "predefined", id } : undefined
    }
  }
}

export interface ITextureMaterial extends ITexInfoBase {
  category?: PredefinedTextureCategory;
  id: string;
}

export function getTextureMaterialFromFileId(fileId: string, token: string, imageSize?: ISize2): ITextureMaterial {
  return {
    id: fileId, category: "custom", image: { type: "userFile", fileId, token, imageSize }
  }
}

export function getPropertyTextureMaterials(userFiles: IUserFileInfo[]): ITextureMaterial[] {
  const materials = [
    ...PredefinedMaterials.getMaterialArray(),
    ...userFiles.filter((f) => f.type === "texture").map<ITextureMaterial>((v) => getTextureMaterialFromFileId(v.id, v.token, v.imageSize))
  ]
  return materials;
}
